import React, { useEffect, useRef, useState } from 'react';
import { TemplateType } from './config/views';
import { useDeviceControls } from './context/deviceControlContext';
import { MediaPlayer } from '@cube3/ui/src/previews/VideoPreview/MediaPlayer';

export type VideoPlayerStatus = {
  playing?: boolean;
  loop?: boolean;
  frameRate?: number
};

type Props = VideoPlayerStatus & {
  src: string;
  type: 'video' | 'image';
  template: TemplateType;
  videoRef?: MediaPlayer;
  mediaElement?: HTMLVideoElement;
};

export const SocialMediaMockupLayer = React.memo((props: Props) => {
  const {
    src,
    type,
    template,
    playing,
    loop,
    frameRate,
    videoRef,
    mediaElement
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [iframeWindow, setIframeWindow] = useState(null);
  const [textureReady, setTextureReady] = useState(false);
  const [seekTo, setSeekTo] = useState(undefined);

  const { theme, viewMode, background } = useDeviceControls();

  const inExploreView = template.includes('instagram') && viewMode === 2; // Instagram 'Explore' view
  const shouldAutoPlay = inExploreView;
  // Autoplay the player if it should (depends on the view) only when the video texture is ready.
  useEffect(() => {
    if (
      textureReady &&
      shouldAutoPlay &&
      mediaElement &&
      videoRef &&
      mediaElement.paused
    ) {
      videoRef.playPause(true);
      if (!loop) {
        videoRef.toggleLoop();
      }
      // force the video texture locate to the previous frame
      setSeekTo(mediaElement.currentTime);
    }
  }, [textureReady, shouldAutoPlay]);

  // When the iframe is mounted, it should initialize the playedTime, and pause the video player to prevent desynchronization issues.
  useEffect(() => {
    if (textureReady && mediaElement && !seekTo && videoRef) {
      videoRef.playPause(false);
      setSeekTo(mediaElement.currentTime);
    }
  }, [textureReady]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data.type === 'MOCKUP_READY') {
        setIframeReady(true);
        setIframeWindow(event.source);
      }
      if (event.data.type === 'TEXTURE_READY' && event.data.src === src) {
        setTextureReady(true);
      }
      if (event.data.type === 'SEEK_TO' && event.data.src === src) {
        setSeekTo(event.data.seekTo);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [src]);

  // send props to social media iframe
  useEffect(() => {
    if (iframeReady && iframeWindow) {
      iframeWindow.postMessage(
        {
          type: 'LOAD_ASSET',
          mediaUrl: src,
          mediaType: type,
          templateId: template,
          seekTo: seekTo,
          playing,
          loop,
          frameRate
        },
        window.location.origin
      );
    }
  }, [
    src,
    type,
    template,
    playing,
    seekTo,
    loop,
    frameRate,
    iframeReady,
    iframeWindow
  ]);

  useEffect(() => {
    if (!iframeWindow) return;
    iframeWindow.postMessage(
      {
        type: 'INITIALIZE_DEVICE_CONTROLS',
        theme,
        viewMode,
        background
      },
      window.location.origin
    );
  }, [theme, iframeWindow, viewMode, background]);

  return (
    <iframe
      ref={iframeRef}
      src="/mockup.html"
      title="Social Media Mockup Iframe"
      sandbox="allow-same-origin allow-scripts"
      width="100%"
      height="100%"
      style={{ border: 'none' }}
    />
  );
}, (prev, next) => {
  // ignore playedSeconds to prevent over-rendering
  return (
    prev.src === next.src &&
    prev.type === next.type &&
    prev.template === next.template &&
    prev.playing === next.playing &&
    prev.loop === next.loop &&
    prev.frameRate === next.frameRate &&
    prev.videoRef === next.videoRef &&
    prev.mediaElement === next.mediaElement
  );
})
